import { MulticallRetryFunction, GenericTokenList } from '../../types'
import { composeVaultDisplayName } from '../displayName'
import { Erc20MetaAbi, Erc4626ConvertAbi, Erc4626TotalAssetsAbi } from './abis'
import { fetchAaveEarnApiVaults, type AaveApiVault } from './api'
import {
  getAaveEarnRegistry,
  hasAaveEarnVaults,
  resolveAaveEarnCuratorName,
} from './registry'
import type { AaveEarnVaults } from './types'

const BRAND = 'Aave'

/** Coerce a multicall cell to bigint; `undefined` on any failure so the
 *  per-entry parse can bail without poisoning the batch. */
const toBigInt = (v: unknown): bigint | undefined => {
  if (v === undefined || v === null) return undefined
  if (typeof v === 'bigint') return v
  if (typeof v === 'number') return BigInt(v)
  if (typeof v === 'string') {
    if (v === '' || v === '0x' || v === '0X') return undefined
    try {
      return BigInt(v)
    } catch {
      return undefined
    }
  }
  return undefined
}

const num = (v: string | null | undefined): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * One-shot fetcher for all tracked Aave Earn vaults on a chain.
 *
 * Hybrid provenance (mirrors GMX's API + on-chain shape):
 *  1. **API leg** — {@link fetchAaveEarnApiVaults} resolves the tracked
 *     curators/vaults into live `Vault` records (identity, underlying
 *     reserve, `vaultApr`, fee, per-underlying USD price).
 *  2. **On-chain leg** — one multicall over the discovered vaults reads
 *     `totalAssets()` (the canonical TVL — the API `balance` field reads the
 *     vault's ~0 idle underlying, since it supplies into Aave and holds
 *     aTokens), share `decimals()`, `totalSupply()`, and
 *     `convertToAssets(10**shareDecimals)` (the share price).
 *
 * Returns `{}` (no network I/O) when the chain has no tracked curators or
 * pinned vaults. Never throws — the orchestrator logs provider-level
 * failures.
 */
export const fetchAaveEarnVaults = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<AaveEarnVaults> => {
  if (!hasAaveEarnVaults(chainId)) return {}

  const reg = getAaveEarnRegistry(chainId)
  const items = await fetchAaveEarnApiVaults(chainId, {
    owners: reg.curators.map((c) => c.owner),
    vaults: reg.vaults,
  })

  // Keep only well-formed items (address + underlying + underlying decimals)
  // so the multicall slicing stays aligned with the parse loop.
  const usable = items.filter(
    (v): v is AaveApiVault & { address: string } =>
      !!v?.address &&
      !!v?.usedReserve?.underlyingToken?.address &&
      v.usedReserve.underlyingToken.decimals != null,
  )
  if (usable.length === 0) return {}

  // On-chain enrichment: 4 calls per vault. The API's `balance` is NOT the
  // vault's TVL — Aave Earn vaults supply into Aave and hold aTokens, so the
  // underlying `balance` reads ~0. `totalAssets()` is the canonical AUM.
  // `convertToAssets` needs the share unit; Aave Earn shares share the
  // underlying's decimals, so we seed it from the API's underlying decimals
  // (and read `decimals()` alongside to carry the true share decimals through).
  const CALLS_PER_VAULT = 4
  const calls = usable.flatMap((v) => {
    const underlyingDec = v.usedReserve!.underlyingToken!.decimals as number
    const shareUnit = 10n ** BigInt(underlyingDec)
    return [
      { address: v.address, name: 'totalAssets', params: [] },
      { address: v.address, name: 'decimals', params: [] },
      { address: v.address, name: 'totalSupply', params: [] },
      { address: v.address, name: 'convertToAssets', params: [shareUnit] },
    ]
  })
  const abis = usable.flatMap(() => [
    Erc4626TotalAssetsAbi,
    Erc20MetaAbi,
    Erc20MetaAbi,
    Erc4626ConvertAbi,
  ])

  const rawResults =
    calls.length > 0
      ? await multicallRetry({
          chain: chainId,
          calls: calls as unknown as never[],
          abi: abis as unknown as never,
        })
      : []

  const out: AaveEarnVaults = {}
  for (let i = 0; i < usable.length; i++) {
    const v = usable[i]
    const base = i * CALLS_PER_VAULT
    const slice = rawResults.slice(base, base + CALLS_PER_VAULT)

    const addressLc = v.address.toLowerCase()
    const token = v.usedReserve!.underlyingToken!
    const underlyingLc = (token.address as string).toLowerCase()
    const assetDecimals = token.decimals as number

    // Share decimals from on-chain `decimals()`, else the underlying's.
    const shareDecOnChain = toBigInt(slice[1])
    const decimals =
      shareDecOnChain !== undefined ? Number(shareDecOnChain) : assetDecimals

    // totalSupply is on-chain only; drop the vault if it didn't resolve.
    const totalSupply = toBigInt(slice[2])
    if (totalSupply === undefined) continue

    const shareUnit = 10n ** BigInt(decimals)
    const underlyingUnit = 10n ** BigInt(assetDecimals)
    // convertToAssets(shareUnit) → raw underlying per 1 share.
    const convertToAssets = toBigInt(slice[3]) ?? 0n
    // convertToShares(underlyingUnit) → raw shares per 1 underlying.
    const convertToShares =
      convertToAssets > 0n ? (shareUnit * underlyingUnit) / convertToAssets : 0n

    // TVL from the canonical ERC-4626 `totalAssets()` (raw underlying). The
    // API `balance` field reads the vault's idle underlying, which is ~0 once
    // it has supplied into Aave — so it undercounts AUM. Fall back to
    // `totalSupply × pricePerShare` if the call reverted.
    const totalAssets =
      toBigInt(slice[0]) ?? (totalSupply * convertToAssets) / shareUnit
    const totalAssetsFormatted = Number(totalAssets) / 10 ** assetDecimals

    const asset = tokenList[underlyingLc]
    const priceUsd =
      prices[underlyingLc] ??
      (v.balance?.usdPerToken != null ? num(v.balance.usdPerToken) : undefined)
    // Derive USD from the correct on-chain TVL × price — the API's
    // `balance.usd` is computed off the wrong (idle) balance.
    const totalAssetsUsd =
      priceUsd !== undefined ? totalAssetsFormatted * priceUsd : 0

    const supplyRate = num(v.vaultApr?.formatted)
    const rewardsRate = 0
    const depositRate = supplyRate + rewardsRate
    const fee = num(v.fee?.value)

    const symbol = (v.shareSymbol ?? '').trim()
    const shareName = (v.shareName ?? '').trim()
    const name = shareName || symbol
    const assetForName =
      asset ??
      (token.symbol
        ? {
            chainId,
            address: underlyingLc,
            symbol: token.symbol,
            decimals: assetDecimals,
          }
        : undefined)
    // Curator classification: Aave Earn vaults are curator-run — the vault
    // `owner` is the curator, NOT Aave (Aave is only the underlying protocol).
    // Label precedence, so we never mislabel a third-party vault as Aave-
    // curated:
    //   1. a known curator name from the registry (`${curator} ${symbol}`),
    //   2. the vault's own curator-set share name (e.g. "Limassol Vault",
    //      "Nova Curated Aave USDC", "Pintu_ETH_USDT"),
    //   3. a generic "Aave <symbol>" only for genuinely unnamed vaults.
    const curatorName = resolveAaveEarnCuratorName(chainId, v.owner)
    const displayName = curatorName
      ? composeVaultDisplayName(BRAND, curatorName, assetForName, name)
      : shareName ||
        composeVaultDisplayName(BRAND, undefined, assetForName, symbol)

    out[addressLc] = {
      address: addressLc,
      underlying: underlyingLc,
      symbol,
      name,
      displayName,
      ...(curatorName ? { curatorName } : {}),
      owner: (v.owner ?? '').toLowerCase(),
      decimals,
      assetDecimals,
      totalAssets: totalAssets.toString(),
      totalSupply: totalSupply.toString(),
      convertToAssets: convertToAssets.toString(),
      convertToShares: convertToShares.toString(),
      supplyRate,
      rewardsRate,
      depositRate,
      fee,
      isErc4626: true,
      asset,
      priceUsd,
      totalAssetsFormatted,
      totalAssetsUsd,
    }
  }

  return out
}
